import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Contractul propunerilor de schimbare (v4/api, App\Changes\*) - perechea
 * lui `application/controllers/Changes.php`.
 *
 * Numele campurilor sunt exact cele pe care le scrie serverul; forma
 * raspunsului e inghetata in `src/changes/__fixtures__/`, scrisa din raspunsul
 * viu al probei din PHP (WRITE_FIXTURES=1), si citita si de proba din browser.
 */

export type AgentMode = 'incident' | 'problem' | 'change' | 'full'

export type DraftStatus = 'proposed' | 'approved' | 'running' | 'done' | 'stopped' | 'rejected'

export type DeviceResultState = 'waiting' | 'ok' | 'error' | 'canceled'

export interface AgentModeInfo {
  mode: AgentMode
  set_by: string
  set_at: string
}

/** Un buton din comutatorul de mod - `$modeMeta` din `changes/_panel.php`. */
export interface ModeMeta {
  label: string
  color: string
  hint: string
}

export interface DraftDevice {
  id: number
  name: string
}

export interface DeviceResult {
  state: DeviceResultState
  note: string
}

/** O propunere, asa cum o listeaza `Drafts::ofCompany` + masinile ei. */
export interface Draft {
  ID: number
  mode: 'change' | 'incident'
  title: string
  intent: string
  command_type: string
  command: string
  waves: string
  stop_after_failures: number
  status: DraftStatus
  proposed_by: 'ai' | 'human' | 'plan'
  approved_by_name: string
  approved_at: string
  waves_sent: number
  succeeded: number
  failed: number
  ticket_id: number
  change_ticket_id: number
  cab_state: 'none' | 'pending' | 'approved' | 'rejected' | 'standard'
  consent_state: '' | 'pending' | 'given' | 'declined'
  window_start: string
  window_end: string
  risk_level: number
  backout: string
  requested_by_email: string
  plan_step_id: number
  created_at: string
  /** In ordinea din propunere: prima e canary-ul. */
  device_list: DraftDevice[]
  /** Starea din coada pe fiecare masina; `[]` cand nu s-a trimis nimic inca. */
  device_results: Record<string, DeviceResult> | DeviceResult[]
}

/** Datele ecranului - `Changes::panelData()`. */
export interface ChangesPanel {
  agentMode: AgentModeInfo
  modes: Record<'incident' | 'problem' | 'change', ModeMeta>
  blockers: string[]
  drafts: Draft[]
  /** Cerut din sertarul agentului, doar cu propunerile discutiei lui. */
  onlySession: boolean
  /** Incident / Full Access se deschid numai de administratorii scrisi pe nume. */
  canOpenIncident: boolean
}

/** O propunere vie care atinge o masina - `Drafts::forDevice`. */
export interface DeviceTodoDraft extends Omit<Draft, 'device_list' | 'device_results'> {
  is_canary: boolean
  position: number
  total_devices: number
}

export interface DeviceTodo {
  drafts: DeviceTodoDraft[]
}

/** Raspunsul unei actiuni (approve / reject / stop / done / mode). */
export interface ChangeAnswer {
  ok: boolean
  error?: string | null
  /** La Full Access: codul a plecat (sau e gresit) si ecranul cere campul. */
  need_code?: boolean
  sent_to?: string
}

export function changesPanelQuery(ids: number[] = []) {
  const suffix = ids.length ? `?ids=${ids.map((id) => String(id)).join(',')}` : ''
  return queryOptions({
    queryKey: ['changes', 'panel', ids],
    queryFn: () => apiGet<ChangesPanel>(`/changes${suffix}`),
    /* Nu se tine in cache: fiecare deschidere reconciliaza valurile pe server. */
    staleTime: 0,
  })
}

export function changesModeQuery() {
  return queryOptions({
    queryKey: ['changes', 'mode'],
    queryFn: () => apiGet<AgentModeInfo>('/changes/mode'),
    staleTime: 0,
  })
}

export function deviceTodoQuery(deviceId: number) {
  return queryOptions({
    queryKey: ['changes', 'device', deviceId],
    queryFn: () => apiGet<DeviceTodo>(`/changes/device/${deviceId}`),
  })
}

export function setAgentMode(mode: AgentMode, code = '', agree = ''): Promise<ChangeAnswer> {
  const fields: Record<string, string> = { mode }
  if (code !== '') fields.code = code
  if (agree !== '') fields.agree = agree
  return apiPost<ChangeAnswer>('/changes/mode', fields)
}

export type DraftAction = 'approve' | 'reject' | 'stop' | 'done'

export function actOnDraft(id: number, action: DraftAction): Promise<ChangeAnswer> {
  return apiPost<ChangeAnswer>(`/changes/${id}/${action}`, {})
}

/** Starea unei masini dintr-o propunere, sau `null` cand nu s-a trimis nimic ei. */
export function resultOf(draft: Draft, deviceId: number): DeviceResult | null {
  const results = draft.device_results
  if (Array.isArray(results)) return null
  return results[String(deviceId)] ?? null
}
